import * as React from "react";

import Form, { FormComponentProps } from "@ant-design/compatible/es/form";
import { UserOutlined } from "@ant-design/icons";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import FormCard from "@impero-com/common/components/formCard";
import FormCardContainer from "@impero-com/common/components/formCardContainer";
import { withRouter } from "@impero-com/common/components/routerUtils";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { t } from "@impero-com/common/utils/i18n";
import { isEmailValid } from "@impero-com/common/utils/isEmailValid";
import { Button, CardFooter, FormSubtitle, FormTitle } from "@impero-com/ui";
import { Input } from "antd";
import { NavigateFunction } from "react-router-dom";

import { postSendOrgChooserEmail } from "api/generated/authentication/authentication";
import { SendOrgChooserEmailData } from "api/generated/model";

import { ENVIRONMENT } from "../env";

import RightSideText from "./rightSideText";
import * as theme from "./theme.css";

const FormItem = Form.Item;

class ForgotTeam extends React.Component<ForgotTeamProps, ForgotTeamState> {
    constructor(props: ForgotTeamProps) {
        super(props);
        this.state = {
            status: { type: "DEFAULT" },
        };
    }

    handleForgottenTeam = (e: React.FormEvent<any>) => {
        e.preventDefault();
        this.props.form.validateFields(
            (err: any, values: { email: string; captcha: string }) => {
                if (!err) {
                    const { email, captcha } = values;
                    const { ajaxManager } = this.props;
                    const requestData: SendOrgChooserEmailData = {
                        email,
                        humanVerificationResponse: captcha,
                    };
                    ajaxManager
                        .ajax<any, ForgotTeamStatus>(
                            () => postSendOrgChooserEmail(requestData),
                            {
                                component: this,
                                initialStatusValue: "DEFAULT",
                                getSuccessStatus: () => ({ type: "DEFAULT" }),
                                getErrorStatus: () => ({ type: "DEFAULT" }),
                                inFlightStatus: { type: "LOGGING_IN" },
                            }
                        )
                        .then(() =>
                            this.props.navigate(
                                "/check?email=" + encodeURIComponent(email)
                            )
                        );
                }
            }
        );
    };

    render() {
        const { getFieldDecorator } = this.props.form;
        return (
            <FormCardContainer rightSide={<RightSideText />}>
                <FormTitle title={t("LoginFlow_FindOrganization")} />
                <FormSubtitle subtitle={t("LoginFlow_EmailToConfirm")} />
                <FormCard onSubmitForm={this.handleForgottenTeam}>
                    <FormItem>
                        {getFieldDecorator("email", {
                            rules: [
                                {
                                    required: true,
                                    message: t("LoginFlow_InputCorrectEmail"),
                                },
                                {
                                    validator: (
                                        _rule: any,
                                        value: string | undefined,
                                        callback: any
                                    ) => {
                                        if (!isEmailValid(value)) {
                                            callback(
                                                t("LoginFlow_InputCorrectEmail")
                                            );
                                        } else {
                                            callback();
                                        }
                                    },
                                },
                            ],
                        })(
                            <Input
                                prefix={
                                    <UserOutlined
                                        className={theme.iconOpacity}
                                    />
                                }
                                placeholder={t("Login_UserName")}
                            />
                        )}
                    </FormItem>
                    <FormItem>
                        {getFieldDecorator("captcha", {
                            rules: [{ required: true }],
                        })(
                            <div className={theme.hcaptchaContainer}>
                                <HCaptcha
                                    size="normal"
                                    sitekey={
                                        ENVIRONMENT.IMPERO_HCAPTCHA_SITE_KEY
                                    }
                                    onVerify={this.handleCaptchaVerify}
                                />
                            </div>
                        )}
                    </FormItem>
                    <FormItem>
                        <Button
                            id="loginFormSubmitBtn"
                            variant="primary"
                            type="submit"
                            loading={this.state.status.type === "LOGGING_IN"}
                            block
                        >
                            {t("LoginFlow_SendMeAnEmail")}
                        </Button>
                    </FormItem>
                </FormCard>

                <CardFooter>
                    <a href="https://impero.com/get-in-touch/" target="_blank">
                        {t("LoginFlow_DontHaveAccount")}
                    </a>
                </CardFooter>
            </FormCardContainer>
        );
    }

    private handleCaptchaVerify = (captcha: string | null) => {
        this.props.form.setFieldsValue({
            captcha,
        });
    };
}

const WrappedForgotTeam = withRouter(
    Form.create<ForgotTeamProps>()(ForgotTeam)
);

export default WrappedForgotTeam;

interface ForgotTeamProps extends FormComponentProps {
    navigate: NavigateFunction;
    ajaxManager: AjaxManager;
}

type ForgotTeamStatus = { type: "DEFAULT" } | { type: "LOGGING_IN" };
interface ForgotTeamState {
    status: ForgotTeamStatus;
}
